import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {Modal} from '@Components/modal';
import {openPanelInModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {LazyPanel} from '@Components/lazy-panel';
import type {UserMediaPanelProps} from '@Panels/user-media-panel/user-media-panel';
import useWindowSize from '@Hooks/useWindowSize';
import {useAppSelector} from '@/hooks';
import {getBrandKitPickerMobileHeight, getUploadsModalWidth, UPLOADS_MODAL_HEIGHT, UPLOADS_MODAL_MOBILE_HEIGHT} from './user-media-modal.library';

const PANEL_ID = 'user-media-modal-panel';

export type UserMediaModalProps = UserMediaPanelProps;

export default function UserMediaModal(): ReactElement {
  const modalData = useAppSelector((state) => {
    return state.modals.modalsHashmap[MODALS_IDS.USER_MEDIA_MODAL];
  });
  const sizeData = useAppSelector((state) => {
    return state.panels.sizeDataHashmap[PANEL_ID];
  });

  const stickToBottom = useAppSelector((state) => {
    return state.panels.panelPropertiesHashmap[PANEL_ID]?.stickToBottom;
  });
  const {windowWidth, windowHeight} = useWindowSize();

  useEffect((): void => {
    if (sizeData?.height) {
      setHeight(sizeData.height);
    }

    if (sizeData?.width) {
      setWidth(sizeData.width);
    }
  }, [sizeData]);

  /** A brand kit's picker opens at the mobile handoff's height and snaps between half and that, not full screen. */
  const isBrandKitPicker = Boolean(modalData.panelProps?.isBrandKitPicker);
  const brandKitPickerMobileHeight = getBrandKitPickerMobileHeight(windowHeight);

  const [height, setHeight] = useState(UPLOADS_MODAL_HEIGHT);
  const [width, setWidth] = useState(getUploadsModalWidth(windowWidth));

  return (
    <Modal
      className="user-media-modal"
      modalId={MODALS_IDS.USER_MEDIA_MODAL}
      stickToBottom={stickToBottom}
      mobileHeight={isBrandKitPicker ? `${brandKitPickerMobileHeight}px` : UPLOADS_MODAL_MOBILE_HEIGHT}
      isBottomSheetOnMobile
      doesBottomSheetHaveSnapPoints
      snapPointsForBottomSheet={isBrandKitPicker ? [windowHeight / 2, brandKitPickerMobileHeight] : undefined}
      panelId={PANEL_ID}
      modalHeight={height}
      modalWidth={width}
    >
      <LazyPanel
        panelDirectoryName="user-media-panel"
        panelProps={{
          ...modalData.panelProps,
          panelId: PANEL_ID,
        }}
      />
    </Modal>
  );
}

export const openUserMediaModal = (props: UserMediaModalProps): void => {
  window.PMW.redux.store.dispatch(
    openPanelInModal({
      modalId: MODALS_IDS.USER_MEDIA_MODAL,
      panelProps: props,
    })
  );
};
